import { useState, useEffect } from 'react';
import { createTrackingEntriesExport, getSuggestionsEntriesStats, getTrackingEntries, getTrackingEntriesByDateRange } from '../../services/usageService';
import { UsageTrackingEntry } from '../../interfaces/usage';
import { getMonthDays } from '../../utils/time/getMonthDays';
import { addMonths, subDays } from 'date-fns';


export interface UseUsageTracker {
  (loadStats?: boolean): {
    trackingEntries: UsageTrackingEntry[];
    loading: boolean;
    error: string | null;
    loadTrackingEntries: LoadTrackingEntries;
    page: number;
    setPage: (page: number) => void;
    hasMore: boolean;
    totalEntries: number;
    changeYear: ChangeYear;
    changeMonth: ChangeMonth;
    setFromDay: (day: number) => void;
    setToDay: (day: number) => void;
    fromDay: number;
    toDay: number;
    fromYear: number;
    toYear: number;
    fromMonth: number;
    toMonth: number;
    fromMonthDays: number;
    toMonthDays: number;
    loadTrackingEntriesByDateRange: LoadTrackingEntriesByDateRange;
    isDateChanged: boolean;
    setIsDateChanged: (isDateChanged: boolean) => void;
    exportLoading: boolean;
    exportError: string | null;
    loadTrackingEntriesExport: LoadTrackingEntriesExport;
    csvData: any[] | null;
    selectedCategory: string;
    handleCategoryChange: (category: string) => void;
    subTypeStats: SubTypeStats[];
    loadSuggestionsEntriesStatsByDateRange: LoadSuggestionsEntriesStatsByDateRange;
  }
}

export interface LoadTrackingEntries {
  (page: number): Promise<void>;
}

export interface ChangeYear {
  (year: number, toFrom: 'from' | 'to'): void;
}

export interface ChangeMonth {
  (month: number, toFrom: 'from' | 'to'): void;
}

export interface LoadTrackingEntriesByDateRange {
  (
    loadPage?: number
  ): Promise<void>;
}

export interface LoadTrackingEntriesExport {
  (): Promise<void>;
}

const CSV_HEADERS = [
  'Date',
  'External Ticket ID',
  'Suggestion Type',
  'Suggestion Subtype',
  'Is Approved',
  'Is Reported Wrong',
  'Customer Message',
  'Suggested Message',
  'Agent Response'
];

export interface DateRange {
  fromYear: number;
  fromMonth: number;
  fromDay: number;
  toYear: number;
  toMonth: number;
  toDay: number;
}

export interface SubTypeStats {
  approved_count: number;
  rejected_count: number;
  ignored_count: number;
  suggestion_subtype: string;
}

export interface LoadSuggestionsEntriesStatsByDateRange {
  (dateRange: DateRange): Promise<void>;
}

const useUsageTracker: UseUsageTracker = (loadStats: boolean = false) => {
  const [trackingEntries, setTrackingEntries] = useState<UsageTrackingEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [totalEntries, setTotalEntries] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [fromYear, setFromYear] = useState<number>(2024);
  const [toYear, setToYear] = useState<number>(2024);
  const [fromMonth, setFromMonth] = useState<number>(11);
  const [toMonth, setToMonth] = useState<number>(12);
  const [fromDay, setFromDay] = useState<number>(1);
  const [toDay, setToDay] = useState<number>(getMonthDays(2024, 12));
  const [fromMonthDays, setFromMonthDays] = useState<number>(getMonthDays(2024, 11));
  const [toMonthDays, setToMonthDays] = useState<number>(getMonthDays(2024, 12));
  const [isDateChanged, setIsDateChanged] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [csvData, setCsvData] = useState<any[] | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('ANSWERING');
  const [subTypeStats, setSubTypeStats] = useState<SubTypeStats[]>([]);

  useEffect(() => {
    if (loadStats) {
      setInitialDateRangeAndLoadStats();
    } else {
      loadTrackingEntries(page);
    }
  }, []);

  const setInitialDateRangeAndLoadStats = async () => {
    const today = new Date();
    const fromDate = addMonths(today, -2);
    const toDate = subDays(today, 1);

    const fromDayVal = fromDate.getDate();
    const toDayVal = toDate.getDate();
    const fromMonthVal = fromDate.getMonth() + 1;
    const toMonthVal = toDate.getMonth() + 1;
    const fromYearVal = fromDate.getFullYear();
    const toYearVal = toDate.getFullYear();

    setFromDay(fromDayVal);
    setToDay(toDayVal);
    changeYear(fromYearVal, 'from');
    changeMonth(fromMonthVal, 'from');
    changeYear(toYearVal, 'to');
    changeMonth(toMonthVal, 'to');

    const dateRange = {
      fromYear: fromYearVal,
      fromMonth: fromMonthVal,
      fromDay: fromDayVal,
      toYear: toYearVal,
      toMonth: toMonthVal,
      toDay: toDayVal
    }
    setIsDateChanged(true);
    loadSuggestionsEntriesStats(page, dateRange);
  }

  const changeYear: ChangeYear = (year, toFrom) => {
    setIsDateChanged(true);
    if (toFrom === 'from') {
      setFromYear(year);
      setFromMonthDays(getMonthDays(year, fromMonth));
    } else {
      setToYear(year);
      setToMonthDays(getMonthDays(year, toMonth));
    }
  };

  const changeMonth: ChangeMonth = (month, toFrom) => {
    setIsDateChanged(true);
    if (toFrom === 'from') {
      setFromMonth(month);
      setFromMonthDays(getMonthDays(fromYear, month));
    } else {
      setToMonth(month);
      setToMonthDays(getMonthDays(toYear, month));
    }
  };

  const loadTrackingEntriesByDateRange: LoadTrackingEntriesByDateRange = async (
    loadPage
  ) => {
    setLoading(true);
    setError(null);
    const usedPage = loadPage || page;
    if (loadPage) {
      setPage(usedPage);
    }
    try {
      const response = await getTrackingEntriesByDateRange(
        fromYear, 
        fromMonth, 
        fromDay, 
        toYear, 
        toMonth, 
        toDay, 
        usedPage, 
        pageSize
      );

      const data: {
        entries: UsageTrackingEntry[],
        total_count: number,
        page: number,
        page_size: number
      } = response.data;

      if (isDateChanged) {
        setTrackingEntries(data.entries);
        setTotalEntries(data.total_count);
        setHasMore((pageSize * page) < data.total_count);
      } else {
        setTrackingEntries([...trackingEntries, ...data.entries]);
      }

    } catch (err: any) {
      const error = err?.message || err?.detail || err?.error || `${err}`;
      console.error(error);
      setError(error);
    } finally {
      setLoading(false);
      setIsDateChanged(false);
    }
  };

  const loadTrackingEntries: LoadTrackingEntries = async (page) => {
    setPage(page);
    setLoading(true);
    setError(null);
    try {
      const response = await getTrackingEntries(page, pageSize);
      if (Array.isArray(response.data.tracking_entries)) {
        setTrackingEntries([...trackingEntries, ...response.data.tracking_entries]);
      }
      setTotalEntries(response.data.total_count);
      setHasMore((pageSize * page) < response.data.total_count);
    } catch (err) {
      setError(`${err}`);
    } finally {
      setLoading(false);
    }
  };

  const loadTrackingEntriesExport: LoadTrackingEntriesExport = async () => {
    setExportLoading(true);
    setExportError(null);
    try {
      const exportPageSize = trackingEntries.length;
      const response = await createTrackingEntriesExport(fromYear, fromMonth, fromDay, toYear, toMonth, toDay, exportPageSize);

      if (response.data) {
        setExportLoading(false);

        if (Array.isArray(response.data)) {
          const csvDict = [
            ...response.data.map((entry: UsageTrackingEntry) => {
              const dateData = entry.created_at as string;
              return {
                date: dateData.split('T')[0] || '',
                external_ticket_id: entry.external_ticket_id || '',
                suggestion_type: entry.suggestion_type || '',
                suggestion_subtype: entry.suggestion_subtype || '',
                is_approved: entry.is_approved ? 'Yes' : 'No',
                is_reported_wrong: entry.is_reported_wrong ? 'Yes' : 'No', 
                customer_message: entry.suggestion_data?.customer_message?.body || '',
                answer_message: entry.suggestion_data?.answer_message || '',
                agent_message: entry.suggestion_data?.agent_message?.body || ''
              }
            })
          ];
          setCsvData(csvDict);
        }
      }

    } catch (err: any) {
      const error = err?.message || err?.detail || err?.error || `${err}`;
      console.error(error);
      setExportError(error);
    } finally {
      setExportLoading(false);
    }
  };

  const handleCategoryChange = async (category: string) => {
    setSelectedCategory(category);
    setSubTypeStats([]);
    setTrackingEntries([]);
    setTotalEntries(0);
    setHasMore(true);
    setPage(1);
    await loadSuggestionsEntriesStats(1, null, category);
  };

  const loadSuggestionsEntriesStats = async (
    page: number = 1, 
    dateRange: DateRange | null = null,
    category: string | null = null
  ) => {
    const usedCategory = category || selectedCategory;
    setLoading(true);
    setError(null);
    const usedDateRange = dateRange || {
      fromYear,
      fromMonth,
      fromDay,
      toYear,
      toMonth,
      toDay
    };
    try {
      const response: {
        data: {
          tracking_entries: {
            entries: UsageTrackingEntry[],
            page: number,
            page_size: number,
            total_count: number
          };
          entries_counts: {
            entries: SubTypeStats[]
          };
          total_count: number;
          page: number;
          page_size: number;
          suggestion_subtypes: string[];
        }
      } = await getSuggestionsEntriesStats(
        usedDateRange.fromYear, 
        usedDateRange.fromMonth, 
        usedDateRange.fromDay, 
        usedDateRange.toYear, 
        usedDateRange.toMonth, 
        usedDateRange.toDay, 
        usedCategory, 
        page, 
        pageSize
      );

      const data = response.data;

      if (Array.isArray(data.tracking_entries.entries)) {
        if (page === 1) {
          setTrackingEntries(data.tracking_entries.entries);
        } else {
          setTrackingEntries([...trackingEntries, ...data.tracking_entries.entries]);
        }
      } else {
        setTrackingEntries([]);
      }
      setTotalEntries(data.tracking_entries.total_count);
      setHasMore((pageSize * page) < data.tracking_entries.total_count);
      if (Array.isArray(data.entries_counts.entries)) {
        setSubTypeStats(data.entries_counts.entries);
      }
    } catch (err) {
      setError(`${err}`);
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestionsEntriesStatsByDateRange: LoadSuggestionsEntriesStatsByDateRange = async (
    dateRange: DateRange
  ) => {
    setLoading(true);
    setError(null);
    await loadSuggestionsEntriesStats(1, dateRange);
  }

  return {
    trackingEntries,
    loading,
    error,
    loadTrackingEntries,
    page,
    setPage,
    hasMore,
    totalEntries,
    changeYear,
    changeMonth,
    setFromDay,
    setToDay,
    fromDay,
    toDay,
    fromYear,
    toYear,
    fromMonth,
    toMonth,
    fromMonthDays,
    toMonthDays,
    loadTrackingEntriesByDateRange,
    isDateChanged,
    setIsDateChanged,
    exportLoading,
    exportError,
    loadTrackingEntriesExport,
    csvData,
    selectedCategory,
    handleCategoryChange,
    subTypeStats,
    loadSuggestionsEntriesStatsByDateRange
  };
};

export default useUsageTracker;
